// These error codes and their corresponding messages are set in the API.
// https://github.com/Tinybop/schools-server/blob/master/src/TBSchools/Basics/ErrorMessages.hs
// If an error is not listed below, it uses the message provided by the API.
const API_ERROR_RESPONSES = {
  0: {
    // This is a catch-all for when users make an API request
    // but are not an authorized user.
    statusCode: 401,
    developerMessage: 'Unauthorized',
    errorType: 'UNAUTHORIZED_ERROR',
    msgId: 'errors.unauthorized',
  },
  9: {
    // This is used when making an API request and the request
    // body is missing a key/value pair.
    statusCode: 422,
    developerMessage: 'Required key not present',
    errorType: 'SERVER_COMMUNICATION_ERROR',
    msgId: 'errors.server_communication',
  },
  16: {
    // If you login with a bad username and password combination
    statusCode: 401,
    developerMessage: 'Incorrect username/password',
    errorType: 'BAD_LOGIN_ERROR',
    msgId: 'errors.bad_login',
  },
  22: {
    // This is when you are creating multiple students and there
    // was an error creating at least one of them.
    statusCode: 422,
    developerMessage: 'Student creation errors',
  },
  24: {
    // This is when the data posted in an API request is...not JSON.
    statusCode: 422,
    developerMessage: 'Non-JSON message body',
    errorType: 'SERVER_COMMUNICATION_ERROR',
    msgId: 'errors.server_communication',
  },
  25: {
    // This is when the data posted in an API request is...also not JSON.
    statusCode: 422,
    developerMessage: 'Message body is not a JSON object',
    errorType: 'SERVER_COMMUNICATION_ERROR',
    msgId: 'errors.server_communication',
  },
  27: {
    // When the body of the API request has unreadable data
    statusCode: 422,
    developerMessage: 'The API request had a malformed value',
    errorType: 'SERVER_COMMUNICATION_ERROR',
    msgId: 'errors.server_communication',
  },
  29: {
    // Currently, you can only send an invite to a teacher.
    // If the API tries to send an invite with a role other than teacher
    // you get this.
    statusCode: 422,
    developerMessage: 'Roles other than teacher requested',
    errorType: 'SERVER_COMMUNICATION_ERROR',
    msgId: 'errors.server_communication',
  },
  30: {
    // If you try to invite a user who already has an active account.
    statusCode: 422,
    developerMessage: 'That user has already accepted an invitation',
    errorType: 'INVITING_EXISTING_USER_ERROR',
    msgId: 'errors.inviting_existing_user',
  },
  31: {
    // This is essentially an "Unknown" error
    statusCode: 500,
    developerMessage: "The 'impossible' occurred",
    errorType: 'SERVER_COMMUNICATION_ERROR',
    msgId: 'errors.server_communication',
  },
  32: {
    // Anytime a user is trying to submit something that _must_
    // be an email address, but it is not.
    statusCode: 422,
    developerMessage: 'Invalid email address',
    errorType: 'INVALID_EMAIL_ADDRESS_ERROR',
    msgId: 'errors.invalid_email_address',
  },
  33: {
    // If you are trying to reset your password or create a user
    // from an invite, but the token has expired.
    statusCode: 401,
    developerMessage: 'Expired token',
  },
  34: {
    // If you are trying to reset your password or create a user
    // from an invite, but the token is not provided.
    statusCode: 422,
    developerMessage: 'No token provided',
    errorType: 'SERVER_COMMUNICATION_ERROR',
    msgId: 'errors.server_communication',
  },
  35: {
    // If you are trying to reset your password or create a user
    // from an invite, but the token cannot be parsed.
    // This is a special case handled in errors.js.
    statusCode: 422,
    developerMessage: 'Malformed token',
  },
  38: {
    // The user is logged in with a valid auth token, but
    // they don’t have sufficient priveleges to perform the action.
    statusCode: 403,
    developerMessage: 'Forbidden',
    errorType: 'PERMISSIONS_ERROR',
    msgId: 'errors.permissions',
  },
  42: {
    // When creating a new user from an invite token, you must set their
    // account status to 'active'.
    statusCode: 422,
    developerMessage: 'Non-active account status',
    errorType: 'SERVER_COMMUNICATION_ERROR',
    msgId: 'errors.server_communication',
  },
  45: {
    // If you are trying to reset your password or create a user
    // from an invite, but the token was already used.
    statusCode: 401,
    developerMessage: 'Token already used',
  },
  47: {
    // If a user is trying to change or set their password and it doesn’t
    // meet the requirements.
    statusCode: 422,
    developerMessage:
      'Password must be 8 or more characters, with at least one non-alphabetical',
    errorType: 'PASSWORD_FORMAT_ERROR',
    msgId: 'errors.password_format',
  },
  48: {
    // If something goes horribly wrong when creating a new user
    statusCode: 500,
    developerMessage: 'Error in account creation',
    errorType: 'SERVER_COMMUNICATION_ERROR',
    msgId: 'errors.server_communication',
  },
  49: {
    // When updating/creating a user, they choose a username that is
    // already in use.
    statusCode: 422,
    developerMessage: 'The username is already taken',
    errorType: 'EDUCATOR_USERNAME_EXISTS_ERROR',
    msgId: 'errors.educator_username_exists',
  },
  50: {
    // There was some unknown error creating a student account.
    // "IMPOSSIBLE"
    statusCode: 500,
    developerMessage: 'No PIN for student',
    errorType: 'SERVER_COMMUNICATION_ERROR',
    msgId: 'errors.server_communication',
  },
  60: {
    // If you are trying to reset your password or create a user
    // from an invite, but the token is readable but not in our system.
    // This is a special case handled in errors.js.
    statusCode: 401,
    developerMessage: 'Token does not exist',
  },
  63: {
    // If you make an API request that requires an email to be sent,
    // you need to provide a hostname that matches one in our whitelist.
    // You get in trouble if you don’t!
    statusCode: 422,
    developerMessage: 'Invalid hostname',
    errorType: 'SERVER_COMMUNICATION_ERROR',
    msgId: 'errors.server_communication',
  },
  64: {
    // This is when you are trying to perform any action that would
    // take a user count above a seat limit. The specific errors
    // are in the data object.
    statusCode: 403,
    developerMessage: 'Seat limit exceeded',
    errorType: 'SEAT_LIMIT_EXCEEDED_ERROR',
    msgId: 'errors.seat_limit_exceeded',
  },
  65: {
    // Currently, we are not allowing API requests that create
    // a user with roles in multiple schools
    statusCode: 422,
    developerMessage: 'Users are from more than one school',
    errorType: 'SERVER_COMMUNICATION_ERROR',
    msgId: 'errors.server_communication',
  },
  66: {
    // If a user exists but has no active roles
    statusCode: 401,
    developerMessage: 'User has no active roles',
    errorType: 'INACTIVE_ACCOUNT_ERROR',
    msgId: 'errors.user_has_no_active_roles',
  },
  71: {
    // Currently, we are not allowing API requests that create
    // a user with roles in multiple schools
    statusCode: 422,
    errorType: 'INVALID_INVITE_TOKEN_ERROR',
    msgId: 'errors.invalid_invite_token',
  },
  76: {
    // If you enter the wrong school code
    statusCode: 401,
    developerMessage: 'Incorrect username/password',
    errorType: 'INVALID_SCHOOL_CODE_ERROR',
    msgId: 'errors.invalid_school_code',
  },
  79: {
    // If you login with a bad username and password combination
    statusCode: 401,
    developerMessage: 'Incorrect username/password',
    errorType: 'STUDENT_LOGIN_USERNAME_ERROR',
    msgId: 'errors.student_login_username',
  },
};

export default API_ERROR_RESPONSES;
